import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api.js";
import { useLangStore } from "@/store/lang.js";
import { useTenantContext } from "@/hooks/useTenantContext.js";
import { LanguageSwitcher } from "@/components/LanguageSwitcher.js";
import type { Language } from "@templestay/i18n";

type TenantListItem = {
  id: string;
  slug: string;
  display_order: number;
  card_image_url: string | null;
  name: string;
  card_title: string | null;
  card_body: string | null;
  card_footnote: string | null;
  card_button_url: string | null;
  card_button_label: string | null;
};

const LANDING_STRINGS: Record<Language, { tagline: string; description: string; empty: string; visit: string }> = {
  ko: {
    tagline: "한국의 사찰에서 머물다",
    description: "고요한 자연 속에서 사찰 생활을 경험하고 마음의 평화를 찾아보세요.",
    empty: "등록된 사찰이 없습니다",
    visit: "방문하기",
  },
  en: {
    tagline: "Stay at a Korean Temple",
    description: "Experience temple life in serene nature and discover inner peace.",
    empty: "No temples registered",
    visit: "Visit",
  },
  zh: {
    tagline: "住在韩国的寺庙",
    description: "在宁静的自然中体验寺庙生活，寻找内心的平静。",
    empty: "暂无注册寺庙",
    visit: "前往",
  },
  ja: {
    tagline: "韓国の寺院に泊まる",
    description: "静かな自然の中で寺院生活を体験し、心の平和を見つけましょう。",
    empty: "登録されている寺院がありません",
    visit: "訪問する",
  },
};

function safeParseJson(raw: string | null): Record<Language, string> {
  if (!raw) return { ko: "", en: "", zh: "", ja: "" };
  try {
    return JSON.parse(raw) as Record<Language, string>;
  } catch {
    return { ko: raw, en: raw, zh: raw, ja: raw };
  }
}

function resolveField(parsed: Record<Language, string>, lang: Language): string {
  return parsed[lang] || parsed.ko || "";
}

function TenantCard({ tenant, lang }: { tenant: TenantListItem; lang: Language }) {
  const s = LANDING_STRINGS[lang];

  const parsedName = safeParseJson(tenant.name);
  const parsedCardTitle = safeParseJson(tenant.card_title);
  const parsedCardBody = safeParseJson(tenant.card_body);
  const parsedCardFootnote = safeParseJson(tenant.card_footnote);
  const parsedCardButtonLabel = safeParseJson(tenant.card_button_label);

  const name = resolveField(parsedName, lang);
  const cardTitle = resolveField(parsedCardTitle, lang);
  const cardBody = resolveField(parsedCardBody, lang);
  const cardFootnote = resolveField(parsedCardFootnote, lang);
  const buttonLabel = resolveField(parsedCardButtonLabel, lang) || s.visit;

  const buttonOnly = !!tenant.card_button_url && !name && !cardTitle && !cardBody && !cardFootnote;

  return (
    <div className={`flex flex-col overflow-hidden rounded-2xl ${buttonOnly ? "" : "bg-white shadow-sm ring-1 ring-gray-100 transition-shadow hover:shadow-md"}`}>
      {tenant.card_image_url && (
        <div className="aspect-[4/3] overflow-hidden bg-gray-100">
          <img
            src={tenant.card_image_url}
            alt=""
            className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
            loading="lazy"
          />
        </div>
      )}
      <div className={`flex flex-1 flex-col ${buttonOnly ? "" : "p-5"}`}>
        {name && <h2 className="text-lg font-bold text-gray-900">{name}</h2>}
        {cardTitle && (
          <p className="mt-1 text-sm font-medium text-stone-500">{cardTitle}</p>
        )}
        {cardBody && (
          <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-600 whitespace-pre-line">
            {cardBody}
          </p>
        )}
        {cardFootnote && (
          <small className="mt-3 block text-xs text-gray-400">{cardFootnote}</small>
        )}
        {tenant.card_button_url && (
          <a
            href={tenant.card_button_url}
            className={`${buttonOnly ? "rounded-2xl" : "mt-4 rounded-xl"} inline-flex w-full items-center justify-center bg-stone-800 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-stone-700 active:bg-stone-900`}
          >
            {buttonLabel}
          </a>
        )}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
      <div className="aspect-[4/3] animate-pulse bg-gray-200" />
      <div className="p-5 space-y-3">
        <div className="h-5 w-2/3 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-gray-100" />
        <div className="space-y-1.5">
          <div className="h-3.5 w-full animate-pulse rounded bg-gray-100" />
          <div className="h-3.5 w-5/6 animate-pulse rounded bg-gray-100" />
        </div>
        <div className="h-11 w-full animate-pulse rounded-xl bg-gray-200" />
      </div>
    </div>
  );
}

export function TemplestayLandingPage() {
  const { lang } = useLangStore();
  const { mainSiteConfig } = useTenantContext();
  const s = LANDING_STRINGS[lang];

  const tagline = mainSiteConfig?.intro?.tagline?.[lang] ?? mainSiteConfig?.intro?.tagline?.ko ?? s.tagline;
  const description = mainSiteConfig?.intro?.description?.[lang] ?? mainSiteConfig?.intro?.description?.ko ?? s.description;

  const { data, isLoading } = useQuery({
    queryKey: ["tenants-list"],
    queryFn: () => api.get<TenantListItem[]>("/api/tenants/list"),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const tenants = data ?? [];
  const sorted = [...tenants].sort((a, b) => a.display_order - b.display_order);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-gray-100 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          <span className="text-lg font-bold tracking-widest text-stone-800">TEMPLESTAY</span>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-5xl px-4 py-12 text-center sm:px-6 sm:py-16">
        <h1 className="text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl">
          {tagline}
        </h1>
        <p className="mt-3 text-base text-stone-500 sm:text-lg">{description}</p>
      </section>

      {/* Temple Grid */}
      <main className="mx-auto max-w-5xl px-4 pb-16 sm:px-6">
        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <p className="text-base">{s.empty}</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {sorted.map((tenant) => (
              <TenantCard key={tenant.id} tenant={tenant} lang={lang} />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 text-center text-xs text-gray-400">
        © {new Date().getFullYear()} Templestay
      </footer>
    </div>
  );
}
